import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  AssemblyType,
  AssemblyTypeKey,
  AssemblyTypeValue,
  BackflowAssemblyInfo,
  InstallationStatus,
  InstallationStatusValue,
  JobFormDataBackflow,
} from '../../types/jobFormData';
import { toString } from '../string';

dayjs.extend(customParseFormat);

export const isTestable = (testData: JobFormDataBackflow) => {
  return (
    !testData.unableToTest &&
    !testData.improperlyInstalled &&
    testData.installationStatus !== InstallationStatus.REMOVED
  );
};

export const assemblyEdited = (
  installationStatus?: InstallationStatusValue,
  originalAssembly?: Partial<BackflowAssemblyInfo>,
  formAssembly?: Partial<BackflowAssemblyInfo>
) => {
  if (installationStatus === InstallationStatus.REMOVED) {
    return true;
  }

  if (!originalAssembly) {
    return !!formAssembly?.type;
  }

  if (!formAssembly) {
    return false;
  }

  return (
    originalAssembly.modelId != formAssembly.modelId ||
    toString(originalAssembly.serialNumber) !==
      toString(formAssembly.serialNumber) ||
    originalAssembly.size != formAssembly.size ||
    originalAssembly.type != formAssembly.type ||
    toString(originalAssembly.location) !== toString(formAssembly.location)
  );
};

export const getFinalTestDate = (testData: JobFormDataBackflow): string => {
  if (
    !testData.backflowAssembly ||
    !testData.backflowAssembly.type ||
    testData.unableToTest ||
    testData.improperlyInstalled ||
    testData.installationStatus === InstallationStatus.REMOVED
  ) {
    return '';
  }

  const tests = testData.tests;
  if (tests && tests.length > 0) {
    let date = '';

    tests.forEach((testAttempt) => {
      if (
        testData.backflowAssembly?.type &&
        testAttempt.type !== 'repair' &&
        testAttempt.testDate
      ) {
        date = testAttempt.testDate;
      }
    });

    return date;
  }

  return '';
};

const METER_READING_INPUT_PATTERN = /^\d*\.?\d*$/;

export const isMeterReadingInput = (value: string): boolean =>
  METER_READING_INPUT_PATTERN.test(value);

export const isValidMeterReading = (
  value: string | number | undefined
): boolean => {
  const input = value?.toString() ?? '';
  return /\d/.test(input) && isMeterReadingInput(input);
};

export const meterReadingRequired = (type: AssemblyTypeValue): boolean => {
  switch (type) {
    case AssemblyType.RPDA:
    case AssemblyType.RPDA_2:
    case AssemblyType.DCDA:
    case AssemblyType.DCDA_2:
    case AssemblyType.TYPE_2_BYPASS:
      return true;

    case AssemblyType.RP:
    case AssemblyType.DC:
    case AssemblyType.PVB:
    case AssemblyType.SVB:
    case AssemblyType.AIR_GAP:
    case AssemblyType.AVB:
      return false;

    default:
      return false;
  }
};

const assemblyTypeKeys = Object.keys(AssemblyType) as AssemblyTypeKey[];
export const assemblyTypeKey = (
  type: AssemblyTypeValue
): AssemblyTypeKey | undefined =>
  assemblyTypeKeys.find((key) => AssemblyType[key] === type);

export const parseTestDate = (value: string) => {
  let date = dayjs(value, 'MM/DD/YYYY', true);
  if (!date.isValid()) {
    date = dayjs(value, 'M/D/YYYY', true);
  }
  return date;
};

export const validateTestDate = (testDate: string | undefined): string[] => {
  if (!testDate) {
    return ['Test date is required'];
  }
  const date = parseTestDate(testDate);
  if (!date.isValid()) {
    return [`Test date "${testDate}" is invalid`];
  }
  if (date.isAfter(dayjs().endOf('day'))) {
    return ['Test date cannot be in the future'];
  }
  return [];
};

export const validateTestTimestamp = (timestamp: unknown): string[] => {
  if (timestamp === undefined || timestamp === null || timestamp === '') {
    return [];
  }
  const value =
    typeof timestamp === 'string' && timestamp.trim() !== ''
      ? Number(timestamp)
      : timestamp;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return ['Test timestamp is invalid'];
  }
  if (value < 0) {
    return ['Test timestamp cannot be negative'];
  }
  return [];
};
